import { getAppLocale, getAppLocaleAsObject } from '../app-locale';
import { API_INSTANCE } from '../service-http';
import { DATA_LAYER_EVENT_NAMES, IAC_ANALYTICS_EVENT, api_base_url, possible_starter_selections } from '../../constants';
import { aggregatedReferences } from '../my-product';
import { appConfiguration } from '../app-starter';

function buildVirtualPageViewDataLayerEvent(config, step) {

  const { lang, country } = getAppLocaleAsObject();

  const result = {
    event: DATA_LAYER_EVENT_NAMES.PAGE_VIEW,
    digital_platform: appConfiguration.seamlessEnv ? 'Seamless' : 'SE Main site',
    digital_platform_country: country,
    environment: process.env.REACT_APP_ANALYTICS_ENV,
    page_language: lang.toUpperCase(),
    configurator_name: possible_starter_selections.SOFTWARE_LICENCE === config.configurationType ? 'Software license configurator' : 'Modicon PLC Configurator',
    project_id: config.id,
    page_sub_sub_category: step,
    from_web_view: false
  };

  if (appConfiguration.seamlessSession) {
    result.business_unit = 'MySEFR2';
    result.page_category = 'Configurator PLC';
    result.page_top_category = 'Configurator';
    result.template = 'Products > Configurator';
  } else {
    result.business_division = 'Industrial Automation';
    result.page_category = 'industrial-automation-control';
    result.page_sub_category = 'tools';
    result.page_top_category = 'products';
    result.template = 'Products';
  }

  return result;
}

function buildFileDownloadDataLayerEvent(config, documentName, extension) {
  const result = {
    event: DATA_LAYER_EVENT_NAMES.FILE_DOWNLOAD,
    configurator_name: possible_starter_selections.SOFTWARE_LICENCE === config.configurationType ? 'Software license configurator' : 'Modicon PLC Configurator',
    document_type: extension,
    document_name: documentName
  };
  return result;
}

function buildAddToCartDataLayerEvent(config, eventName) {
  const result = {
    event: DATA_LAYER_EVENT_NAMES.ADD_TO_CART,
    configurator_name: possible_starter_selections.SOFTWARE_LICENCE === config.configurationType ? 'Software license configurator' : 'Modicon PLC Configurator',
    project_id: config.id,
    project_name: config.name,
    cta_name:  eventName,
    add_to_cart_location: config.step,
    ecommerce: {
      items: aggregatedReferences(config.extractReferencesForAddToCart().extractSortedReferences()).map(refObj => {
        return {
          item_id: refObj.reference,
          item_name: refObj.reference,
          quantity: refObj.quantity
        };
      })
    }
  }
  return result;
}

function logDataLayerEvent(payload) {
  if (appConfiguration.seamlessSession) {
    document.dispatchEvent(
      new CustomEvent('track_iac_analytics', {
        bubbles: true,
        detail: payload
      })
    );

  } else {
    if (window.top === window.self) {
      let dataLayerObject = window?.dataLayer;
      if (dataLayerObject != null) {
        try {
          dataLayerObject.push(payload);
        } catch(err) {
          console.warn('Failed to push to the data layer');
        }
      } else {
        console.warn('Data layer is not defined');
      }
    }
     else {
      window.parent.postMessage(
        { type: 'ScWidgetGAEventActionEvent', options: payload }, '*');
    }
  }
}

function logEvent(type, configId, meta) {
  const url = `${api_base_url}/log/event?locale=${getAppLocale()}&type=${type}${configId ? `&id=${configId}` : ''}`
  API_INSTANCE.post(url, meta);
}

function logEventWithTag(type, tag, configId, meta) {
  const url = `${api_base_url}/log/event?locale=${getAppLocale()}&type=${type}&tag=${tag}${configId ? `&id=${configId}` : ''}`
  API_INSTANCE.post(url, meta);
}

function logSurveyEvent(type, tag, configId, meta) {
  const url = `${api_base_url}/log/survey-event?locale=${getAppLocale()}&type=${type}&tag=${tag}${configId ? `&id=${configId}` : ''}`
  API_INSTANCE.post(url, meta);
}

export function logVirtualPageViewDataLayerEvent (config, newStep) {
  logDataLayerEvent(buildVirtualPageViewDataLayerEvent(config, newStep));
}

export function logSavedConfigurationEvent(configuration) {
  logEvent('SAVE_CONFIGURATION', configuration.id, {
    category: configuration.starterType,
    range: configuration.rangeInfo.rangeName,
    step: configuration.step
  });
}

export function logAddToMyProductEvent(config, step) {
  const eventName = 'ADD_TO_MY_PRODUCTS';
  logEventWithTag(eventName, step, config.id, {});
  logDataLayerEvent(buildAddToCartDataLayerEvent(config, eventName));
}

export function logAddToMyProjectEvent(config, step) {
  const eventName = 'ADD_TO_MY_PROJECT';
  logEventWithTag(eventName, step, config.id, {});
  logDataLayerEvent(buildAddToCartDataLayerEvent(config, eventName));
}

export function logAddToMyCartEvent(config, step) {
  const eventName = 'ADD_TO_MY_CART';
  logEventWithTag(eventName, step, config.id, {});
  logDataLayerEvent(buildAddToCartDataLayerEvent(config, eventName));
}

export function logAddToMyListEvent(config, step) {
  const eventName = 'ADD_TO_MY_LIST';
  logEventWithTag(eventName, step, config.id, {});
  logDataLayerEvent(buildAddToCartDataLayerEvent(config, eventName));
}

export function logAddToBuyAutomationEvent(config, step) {
  const eventName = 'ADD_TO_BUY_AUTOMATION';
  logEventWithTag(eventName, step, config.id, {});
  logDataLayerEvent(buildAddToCartDataLayerEvent(config, eventName));
}

export function logContactSalesEvent(config, step) {
  const eventName = 'CONTACT_SALES';
  logEventWithTag(eventName, step, config.id, {});
}

export function logSurveyOpenEvent(surveyType, configId, step) {
  logSurveyEvent(surveyType, step, configId, {});
}

export function logSurveySubmitEvent(surveyType, configId, step, surveyResult) {
  logSurveyEvent(surveyType, step, configId, { 'surveyAnswers': JSON.stringify(surveyResult) });
}

export function logFilterSelectedEvent(configuration, type, filters) {
  logEvent('FILTERS_' + type, configuration.id, filters)
}

export function logDocumentationExportEvent(configuration, eventName, extension) {
  logEvent(`DOWNLOADS_${eventName}`, configuration.id, {});
  logDataLayerEvent(buildFileDownloadDataLayerEvent(configuration, eventName, extension));
}

export function logExportConfiguration(configuration, exportType, extension) {
  logEvent(exportType, configuration.id, {});
  logDataLayerEvent(buildFileDownloadDataLayerEvent(configuration, exportType, extension));
}

export function log3DViewerExportConfiguration(configuration, exportFormat, extension) {
  const eventName = `EXPORT_3D_VIEWER_${exportFormat.toUpperCase()}`;
  logEventWithTag(eventName, configuration.step, configuration.id, {});
  logDataLayerEvent(buildFileDownloadDataLayerEvent(configuration, eventName, extension));
}

export function log3DViewerExportErrorConfiguration(configuration, exportFormat) {
  const eventName = `EXPORT_ERROR_3D_VIEWER_${exportFormat.toUpperCase()}`;
  logEventWithTag(eventName, configuration.step, configuration.id, {});
}

export function logShareConfiguration(configId) {
  logEvent('SHARE_BY_MAIL', configId, {});
}

export function logReopenedConfiguration(configId) {
  logEvent('REOPENED', configId, {});
}

export function logRenamedConfiguration(configId) {
  logEvent('RENAMED', configId, {});
}

export function logViewIOTypes(configuration) {
  logEvent('VIEW_IO_TYPES', configuration.id, {});
}

export function logCSVEvent (config, step) {
  logEventWithTag('EXPORT_CSV', step, config.id, {});
  logDataLayerEvent(buildAddToCartDataLayerEvent(config, 'EXPORT_CSV'));
}

export function logXLSEvent (config, step) {
  logEventWithTag('DOWNLOADS_XLS', step, config.id, {});
  try {
    logDataLayerEvent(buildAddToCartDataLayerEvent(config, 'EXPORT_XLS'));
  } catch(err){};
}

export function logCreateConfigurationEvent (configId, technology, cbrData) {
  logEventWithTag('CREATE', technology, configId, cbrData);
}

export function logCreateSoftwareLicenceConfigurationEvent (configId, technology, cbrData) {
  logEventWithTag('CREATE_SOFTWARE_LICENCE', technology, configId, cbrData);
}

export function logDeleteCBRFilterEvent (configId, step, category) {
  logEventWithTag('DELETE+FILTER', step, configId, {category});
}

export function raiseIACAnalyticsEvent (eventName) {
  try {
    document.dispatchEvent(
      new CustomEvent(IAC_ANALYTICS_EVENT, {
        bubbles: true,
        detail: {
          eventName,
          configuratorName: 'plc'
        }
      })
    );
  } catch(err) {};
}
